import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { Cancel as IconCancel } from '../Icons/Cancel';
import { capitalizeWords, setClass } from '../../helpers/utils';
import { Chip } from '../Chip/index';
import './styles.scss';

type ComboBoxProps = {
	className?: string;
	type?: string;
	id?: string;
	placeholder?: string;
	name?: string;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onChange?: (value: unknown) => void;
	disabled?: boolean;
	displayOptions?: Record<string, unknown>[];
	variant?: string;
	optId?: string;
	optText?: string;
	size?: string;
	hookForm?: any;
	setItems?: any;
	onHandleAlert?: (...args: unknown[]) => void;
	label?: string;
};

const EMPTY_OPTIONS: Record<string, unknown>[] = [];

const isWatchValueClear = (value: unknown): boolean =>
	value == null ||
	value === true ||
	value === '' ||
	(Array.isArray(value) && value.length === 0);

export const ComboBox = ({
	className = '',
	type = 'text',
	id,
	placeholder,
	name,
	onBlur,
	onChange,
	disabled,
	displayOptions = EMPTY_OPTIONS,
	variant,
	optId = 'value',
	optText = 'option',
	size = 'medium',
	hookForm = { formState: { errors: {} } } as any,
	setItems,
	onHandleAlert,
	label, // used for accordion-filters
}: ComboBoxProps) => {
	const isMultiple = variant === 'multiple';

	const [display, setDisplay] = useState(false);
	const [isFocus, setIsFocus] = useState(false);
	const [isFill, setIsFill] = useState(false);
	const [search, setSearch] = useState('');
	const [chips, setChips] = useState<any[]>([]);
	const [filters, setFilters] = useState<any[]>([]);
	const [initChange, setInitChange] = useState(false);
	const [watchInput, setWatchInput] = useState(false);

	const comboBoxEl = useRef<HTMLDivElement | null>(null);
	const inputHidden = useRef<HTMLInputElement | null>(null);
	const justSelected = useRef(false);
	const isEditing = useRef(false);
	const prevSelectedIdRef = useRef<unknown>(undefined);
	const prevSetItemsRef = useRef<unknown>(undefined);
	const prevFiltersRef = useRef<any[]>(filters);
	const prevWatchClearRef = useRef<boolean | null>(null);

	const onChangeRef = useRef(onChange);
	useEffect(() => {
		onChangeRef.current = onChange;
	}, [onChange]);

	const {
		register,
		setValue,
		watch,
		control,
		formState: { errors },
	} = hookForm;

	const watchSearch = watch?.(name, true);

	const watchedValue = useWatch({ control, name });

	const watchedId = useMemo(() => {
		if (isMultiple) return null;
		if (Array.isArray(watchedValue)) {
			return watchedValue[0]?.[optId] ?? null;
		}
		return watchedValue ?? null;
	}, [watchedValue, isMultiple, optId]);

	const isSize = size === 'medium' ? 'is-medium' : '';

	// Mount-only: derive initial fill state from rendered input value.
	useEffect(() => {
		const elem = comboBoxEl.current;
		const input = elem?.querySelector('input');
		setIsFill(!!input && input.value.trim().length > 0);
	}, []);

	// Sync chips/filters from `setItems` only when it actually changes.
	useEffect(() => {
		if (prevSetItemsRef.current === setItems) return;
		prevSetItemsRef.current = setItems;

		if (setItems && setItems !== '') {
			setChips(setItems);
			setFilters(setItems);
			return;
		}
		if (setItems === '') {
			if (chips.length > 0) setChips([]);
			setValue(name, null);
		}
	}, [setItems, chips.length, name, setValue]);

	useEffect(() => {
		if (isMultiple) return;
		if (isEditing.current) return;

		if (justSelected.current) {
			justSelected.current = false;
			prevSelectedIdRef.current = watchedId;
			return;
		}

		if (prevSelectedIdRef.current === watchedId) return;

		if (watchedId == null) {
			prevSelectedIdRef.current = watchedId;
			setFilters((prev) => (prev.length > 0 ? [] : prev));
			setSearch((prev) => (prev !== '' ? '' : prev));
			setValue(`search${name}`, null);
			setIsFill((prev) => (prev ? false : prev));
			return;
		}

		const match = (displayOptions || []).find(
			(option) => option[optId] === watchedId,
		);

		if (match) {
			prevSelectedIdRef.current = watchedId;
			setFilters([match]);
			const text = String(match[optText] ?? '');
			setSearch(text);
			setValue(`search${name}`, text);
			setIsFill(true);
		}
	}, [watchedId, displayOptions, isMultiple, optId, optText, name, setValue]);

	useEffect(() => {
		const valueClear = isWatchValueClear(watchSearch);
		const prevClear = prevWatchClearRef.current;
		prevWatchClearRef.current = valueClear;

		const transitionedToClear = prevClear === false && valueClear;

		if (watchInput && transitionedToClear) {
			if (isMultiple) {
				if (filters.length > 0) setFilters([]);
				if (chips.length > 0) setChips([]);
			} else {
				if (filters.length > 0) setFilters([]);
				if (search !== '') setSearch('');
				if (isFill) setIsFill(false);
			}
			onChangeRef.current?.(name);
		}
	}, [watchSearch, watchInput, isMultiple, name, filters.length, chips.length, search, isFill]);

	useEffect(() => {
		if (prevFiltersRef.current !== filters) {
			prevFiltersRef.current = filters;
			setValue(name, filters);
		}
		if (initChange) {
			onHandleAlert?.(filters.length === 0);
		}
	}, [filters, initChange, name, setValue, onHandleAlert]);

	const handleBlur = useCallback(
		(event: MouseEvent | React.FocusEvent) => {
			const target = (event as any).target as Node | null;
			const box = comboBoxEl.current;
			if (box && target && !box.contains(target)) {
				setIsFocus(false);
				const input = box.querySelector('input');
				setIsFill(!!input && input.value.trim().length > 0);
				setDisplay(false);
			}
			if (typeof onBlur === 'function') {
				onBlur(event as React.FocusEvent<HTMLInputElement>);
			}
		},
		[onBlur],
	);

	useEffect(() => {
		const handler = (event: MouseEvent) => handleBlur(event);
		window.addEventListener('mousedown', handler);
		return () => {
			window.removeEventListener('mousedown', handler);
		};
	}, [handleBlur]);

	const handleSelectOption = (valueInput: Record<string, unknown>) => {
		if (!isMultiple) {
			justSelected.current = true;
			isEditing.current = false;
			prevSelectedIdRef.current = valueInput[optId];
			setSearch(String(valueInput[optText] ?? ''));
			setFilters([valueInput]);
			setDisplay(false);
			onChangeRef.current?.([valueInput]);
		} else {
			const alreadySelected = chips.some(
				(chip) => chip[optId] === valueInput[optId],
			);
			if (alreadySelected) {
				setDisplay(false);
				return;
			}
			const newChips = [...chips, valueInput];
			setChips(newChips);
			setFilters(newChips);
			setDisplay(false);
			setSearch('');
			onChangeRef.current?.(newChips);
		}
		setWatchInput(true);
	};

	const handleRemoveChip = (valueRemove: Record<string, unknown>) => {
		setInitChange(true);
		const nextChips = chips.filter((chip) => chip !== valueRemove);
		const nextFilters = filters.filter((item) => item !== valueRemove);
		setChips(nextChips);
		setFilters(nextFilters);
	};

	const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const nextValue = e.target.value;
		if (!isMultiple) {
			isEditing.current = true;
			justSelected.current = false;
			prevSelectedIdRef.current = null;
			if (filters.length > 0) setFilters([]);
			setValue(name, null);
		}
		setSearch(nextValue);
		onChangeRef.current?.({ name, value: nextValue });
	};

	const filteredOptions = useMemo(() => {
		const q = search.toLowerCase();
		return (displayOptions || []).filter(
			(option) => String(option[optText] ?? '').toLowerCase().indexOf(q) > -1,
		);
	}, [displayOptions, optText, search]);

	const handleWrapperClick = () => {
		if (disabled) return;
		setDisplay((prev) => !prev);
	};

	return (
		<div
			ref={comboBoxEl}
			data-variant={variant}
			className={setClass(
				`input-combo-box combo-box
        ${isFill || isFocus ? 'is-fill' : ''}
        ${display ? 'is-display' : ''}
        ${disabled ? 'is-disabled' : ''}
        ${errors[name] ? 'is-error' : ''}
        ${chips.length > 0 ? 'have-chips-content' : ''}
        ${className}
        ${isSize}`,
			)}
			onClick={handleWrapperClick}
		>
			{chips.length <= 0 && <label>{placeholder}</label>}
			{filters.length > 0 &&
				chips.map((value, i) => {
					return (
						<div key={i} className={setClass('content-chips')}>
							<Chip title={value[optText]} iconClose={true}>
								<span>{capitalizeWords(value[optText])}</span>
								<button
									className="button"
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										handleRemoveChip(value);
									}}
								>
									<IconCancel width={20} height={20}></IconCancel>
								</button>
							</Chip>
						</div>
					);
				})}

			<input
				ref={inputHidden}
				type="hidden"
				name={name}
				disabled={disabled}
				id={id}
				{...register?.(name, {
					value: filters,
				})}
			/>
			<input
				className={setClass(
					`${display ? 'is-display' : ''}
					${chips.length > 0 ? 'have-chips' : ''}`,
				)}
				type={type}
				id={`search${name}`}
				name={`search${name}`}
				value={search}
				disabled={disabled}
				onFocus={() => setIsFocus(true)}
				onClick={(e) => {
					e.stopPropagation();
					if (!disabled) setDisplay((prev) => !prev);
				}}
				placeholder={chips.length > 0 ? `${placeholder}` : ''}
				{...register?.(`search${name}`, {
					onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleBlur(e),
					onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
						handleChangeInput(e),
				})}
				data-label={label}
			/>
			{display && (
				<div className="content-box">
					{filteredOptions.map((value, i) => {
						return (
							<div
								className={setClass(`
                    option
                    ${chips?.some((chip) => chip[optId] === value[optId]) ? 'selected' : ''}
                  `)}
								key={i}
								onClick={(e) => {
									e.stopPropagation();
									handleSelectOption(value);
								}}
							>
								<span className={setClass('')}>
									{capitalizeWords(String(value[optText] ?? ''))}
								</span>
							</div>
						);
					})}
				</div>
			)}
			{errors[name] && (
				<span className="message-error">{(errors[name] as any).message}</span>
			)}
		</div>
	);
};